import React, { useEffect, useState } from 'react';
import { Alert, IconButton, Stack, Typography, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSocket } from '../../context/SocketContext';
import { listMyCallHistory } from '../../services/callsApi';

function dismissedKey(userId) {
  return `aimhi:missed-calls:dismissed:${userId || 'anon'}`;
}

function loadDismissed(userId) {
  try {
    const raw = localStorage.getItem(dismissedKey(userId));
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function persistDismissed(userId, set) {
  try {
    localStorage.setItem(dismissedKey(userId), JSON.stringify([...set]));
  } catch {
    /* ignore quota errors */
  }
}

/**
 * Global client-facing banner that surfaces missed video calls from the last 24h.
 * Mounted under SocketProvider so it refreshes on reconnect (the moment where a
 * push event might have been lost).
 */
export default function MissedCallBanner({ token, currentUser, sx = { mx: 2, mt: 2 } }) {
  const socket = useSocket();
  const [missed, setMissed] = useState([]);
  const [dismissed, setDismissed] = useState(() => loadDismissed(currentUser?._id));

  useEffect(() => {
    setDismissed(loadDismissed(currentUser?._id));
  }, [currentUser?._id]);

  useEffect(() => {
    if (!token || !socket || !currentUser) return;

    const roles = currentUser.role;
    const isClient = Array.isArray(roles) ? roles.includes('client') : roles === 'client';
    if (!isClient) return;

    let cancelled = false;
    (async () => {
      try {
        const { calls } = await listMyCallHistory({ token });
        if (cancelled) return;
        // A call the client never joined is missed once it has ended, or once
        // it can no longer be answered: the server stops offering a ringing call
        // after five minutes, and a call the therapist left without hanging up
        // can stay 'active' for days (9 Sep 2026, AD-51).
        const unanswerableAfterMs = 10 * 60 * 1000;
        const missedOnes = (calls || []).filter(c => !c.clientJoined && (
          ['ended', 'failed'].includes(c.state)
          || (c.state === 'active' && Date.now() - new Date(c.createdAt).getTime() > unanswerableAfterMs)
        ));
        setMissed(missedOnes);
      } catch (err) {
        if (err?.response?.status && err.response.status !== 401) {
          console.warn('[MissedCallBanner] history fetch failed:', err.message);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [socket, token, currentUser]);

  const handleDismiss = (id) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    persistDismissed(currentUser?._id, next);
  };

  const visible = missed.filter(c => !dismissed.has(c.liveCallId));
  if (visible.length === 0) return null;

  return (
    <Box sx={sx}>
      <Stack spacing={1}>
        {visible.map(c => (
          <Alert
            key={c.liveCallId}
            severity="info"
            action={
              <IconButton
                aria-label="Dismiss"
                size="small"
                color="inherit"
                onClick={() => handleDismiss(c.liveCallId)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            <Typography variant="body2">
              You missed a video call from {c.therapistName}
              {' · '}
              {new Date(c.createdAt).toLocaleString()}
            </Typography>
          </Alert>
        ))}
      </Stack>
    </Box>
  );
}
